import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks';

/* ---------- styled components ---------- */
const FormContainer = styled.div`
  width: 100%;
  max-width: 380px;
`;

const AuthTabs = styled.div`
  display: flex;
  background: var(--bg-elevated);
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 2rem;
`;

const AuthTab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.625rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-muted)')};
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--bg-surface)' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-sm)' : 'none')};
  font-family: inherit;
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
`;

const FormSubtitle = styled.p`
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-surface);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: var(--bg-elevated);
    border-color: var(--border-strong);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--text-muted);
  font-size: 0.8125rem;
  margin: 0.5rem 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const InputLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  transition: all 0.2s;
  outline: none;
  width: 100%;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--ring);
  }
`;

const FormRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  cursor: pointer;

  input[type='checkbox'] {
    accent-color: var(--accent);
  }
`;

const ForgotLink = styled.a`
  font-size: 0.8125rem;
  color: var(--accent);
  font-weight: 500;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  white-space: nowrap;
  background: var(--accent);
  color: white;
  width: 100%;

  &:hover {
    background: var(--accent-hover);
    box-shadow: 0 4px 12px rgba(45, 154, 143, 0.3);
  }
`;

const TermsText = styled.p`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ErrorBanner = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.85rem;
  margin-bottom: 16px;
`;

const AuthFooter = styled.div`
  margin-top: 2rem;
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-muted);

  a {
    color: var(--accent);
    font-weight: 500;
    cursor: pointer;
  }
`;

/* ---------- Google SVG icon ---------- */
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

/* ---------- component ---------- */
const Auth: React.FC = () => {
  const [tab, setTab] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const { login, isLoading, error } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tab === 'signin') {
      await login(email, password);
    } else {
      // For signup, use the same mock login for now
      await login(email, password);
    }
  };

  return (
    <FormContainer>
      <AuthTabs>
        <AuthTab $active={tab === 'signin'} onClick={() => setTab('signin')}>
          Sign In
        </AuthTab>
        <AuthTab $active={tab === 'signup'} onClick={() => setTab('signup')}>
          Create Account
        </AuthTab>
      </AuthTabs>

      <FormHeader>
        <FormTitle>
          {tab === 'signin' ? 'Welcome back' : 'Get started'}
        </FormTitle>
        <FormSubtitle>
          {tab === 'signin'
            ? 'Sign in to your Lumina account'
            : 'Create your Lumina account in seconds'}
        </FormSubtitle>
      </FormHeader>

      {error && (
        <ErrorBanner>{error}</ErrorBanner>
      )}

      <Form onSubmit={handleSubmit}>
        <SocialBtn type="button">
          <GoogleIcon />
          Continue with Google
        </SocialBtn>

        <Divider>or</Divider>

        {tab === 'signin' ? (
          <>
            <InputGroup>
              <InputLabel htmlFor="email">Email address</InputLabel>
              <Input
                type="email"
                id="email"
                placeholder="you@company.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup>
              <InputLabel htmlFor="password">Password</InputLabel>
              <Input
                type="password"
                id="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </InputGroup>
            <FormRow>
              <CheckboxLabel>
                <input type="checkbox" /> Remember me
              </CheckboxLabel>
              <ForgotLink>Forgot password?</ForgotLink>
            </FormRow>
            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </SubmitButton>
          </>
        ) : (
          <>
            <InputGroup>
              <InputLabel htmlFor="fullname">Full name</InputLabel>
              <Input
                type="text"
                id="fullname"
                placeholder="Jane Smith"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup>
              <InputLabel htmlFor="signup-email">Email address</InputLabel>
              <Input
                type="email"
                id="signup-email"
                placeholder="you@company.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup>
              <InputLabel htmlFor="signup-password">Password</InputLabel>
              <Input
                type="password"
                id="signup-password"
                placeholder="Create a password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </InputGroup>
            <TermsText>
              By creating an account you agree to our Terms of Service and
              Privacy Policy.
            </TermsText>
            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? 'Creating account...' : 'Create Account'}
            </SubmitButton>
          </>
        )}
      </Form>

      {tab === 'signin' ? (
        <AuthFooter>
          Don&apos;t have an account?{' '}
          <a onClick={() => setTab('signup')}>Create one</a>
        </AuthFooter>
      ) : (
        <AuthFooter>
          Already have an account?{' '}
          <a onClick={() => setTab('signin')}>Sign in</a>
        </AuthFooter>
      )}
    </FormContainer>
  );
};

export default Auth;
